import { useTheme } from "@material-ui/core";
import React, { memo, useCallback, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { CodeInputContext } from "./CodeInput";
const Slot = styled.input `
  width: 100%;
  padding: 10px;

  font-size: 28px;
  font-weight: bold;
  text-align: center;

  background: ${(props) => props.theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.075)" : "#f0f0f0"};

  color: ${(props) => props.theme.palette.text.primary};

  border: 1px solid rgba(0, 0, 0, 0.01);
  border-radius: 3px;
  user-select: none;
  transition: transform 0.175s ease, background 0.1s ease-out;

  ::placeholder {
    color: rgba(0, 0, 0, 0.05);
  }

  ::selection {
    background: transparent;
  }

  &:not([value=""]) {
    color: transparent;
    text-shadow: ${(props) => `0 0 0 ${props.theme.palette.text.primary}`};
  }

  &:hover {
    cursor: pointer;

    transform: translateY(-2px);

    background: ${(props) => props.theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.125)" : "#e3e3e3"};
  }

  &:focus {
    outline: none;
    transform: translateY(-4px);

    background: ${(props) => props.theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.125)" : "#e3e3e3"};

    ::placeholder {
      color: transparent;
    }
  }
`;
export const CodeInputSlot = memo((props) => {
    const { autofocus, values, focus, focusIndex, setFocused, focusNext, focusPrevious } = useContext(CodeInputContext);
    const ref = useRef();
    const theme = useTheme();
    const pattern = props.alphanumeric ? /^[0-9a-zA-Z]+$/ : /^[0-9]+$/;
    useEffect(() => {
        var _a, _b;
        if (focusIndex === props.index) {
            (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
    }, [focusIndex, props.index]);
    const onChange = useCallback((event) => {
        const eventValue = event.target.value;
        // in the case of an autocomplete or copy and paste
        if (eventValue.length > 2) {
            // see if we can use the string to fill out our values
            if (eventValue.length === values.length && eventValue.match(pattern)) {
                props.onChange(eventValue.split(""));
            }
            return;
        }
        // digit was deleted
        if (eventValue === "") {
            const newValues = values.slice();
            newValues[props.index] = "";
            props.onChange(newValues);
            return;
        }
        // we want to override the input value with the last digit typed
        const currentValue = values[props.index];
        let newValue = eventValue;
        if (currentValue[0] === eventValue[0]) {
            newValue = eventValue[1];
        }
        else if (currentValue[0] === eventValue[1]) {
            newValue = eventValue[0];
        }
        // only fire a change event if the new value is a digit
        if (newValue.match(pattern)) {
            const newValues = values.slice();
            newValues[props.index] = newValue;
            props.onChange(newValues);
            // tab to next pin code input if we aren't at end already
            if (props.index < values.length - 1) {
                focusNext();
            }
        }
    }, [focusNext, pattern, props, values]);
    const onKeyDown = useCallback((event) => {
        if ("vibrate" in navigator) {
            navigator.vibrate(50);
        }
        // <-- jump left
        if (event.keyCode === 37) {
            if (props.index > 0) {
                focusPrevious();
            }
        }
        // jump right -->
        if (event.keyCode === 39) {
            if (props.index < values.length - 1) {
                focusNext();
            }
        }
        // if we see a backspace/delete and the input is empty, transfer focus backward
        if (event.key === "Backspace") {
            if (values[props.index] !== "") {
                const newValues = values.slice();
                newValues[props.index] = "";
                props.onChange(newValues);
            }
            else {
                if (props.index > 0) {
                    const newValues = values.slice();
                    newValues[props.index - 1] = "";
                    props.onChange(newValues);
                    focusPrevious();
                }
            }
        }
    }, [focusNext, focusPrevious, props, values]);
    const onFocus = useCallback(() => {
        focus(props.index);
        setFocused(props.index, true);
    }, [focus, props.index, setFocused]);
    const onBlur = useCallback(() => {
        setFocused(props.index, false);
    }, [props.index, setFocused]);
    return (React.createElement(Slot, { autoComplete: "off", ref: ref, theme: theme, name: "betaInputSlot", type: "text", inputMode: props.alphanumeric ? "text" : "numeric", value: values[props.index], autoFocus: autofocus && props.index === 0, onFocus: onFocus, onBlur: onBlur, onChange: onChange, onKeyDown: onKeyDown, onClick: (event) => {
            if ((event === null || event === void 0 ? void 0 : event.target) instanceof HTMLInputElement) {
                event.target.blur();
                event.target.focus();
            }
        } }));
});
//# sourceMappingURL=CodeInputSlot.js.map